import { MinPayRateUnit } from '@/types/jobs';
import { MIN_PAY_RATES } from '@/utils/jobSearchBoxOptions';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, ButtonGroup, InputAdornment, Paper, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import styles from './MinPayRateDropdown.module.scss';

export type MinPayRateDropdownProps = {
  minPayRate: string | null;
  onMinPayRateChange: (value: string | null) => void;
  minPayRateUnit: MinPayRateUnit;
  onMinPayRateUnitChange: (value: MinPayRateUnit) => void;
};

const MinPayRateDropdown = ({
  minPayRate,
  onMinPayRateChange,
  minPayRateUnit,
  onMinPayRateUnitChange,
}: MinPayRateDropdownProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>();
  const paperRef = useRef<HTMLDivElement>();

  // a click on custom buttons in dropdown
  // triggers onBlur event on the input field
  // which closes the dropdown
  // we disable close-on-blur behaviour inside onClose callback
  // and add custom listeners below
  const handleOutsideClick = useCallback(
    e => {
      if (open) {
        if (
          (containerRef.current && containerRef.current.contains(e.target)) ||
          (paperRef.current && paperRef.current.contains(e.target))
        ) {
          // inside click
          return;
        }
        // outside click
        setOpen(false);
      }
    },
    [open],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick]);
  // end of custom listeners

  return (
    <Autocomplete
      value={minPayRate ?? null}
      onChange={(_e, value) => {
        onMinPayRateChange(value);
      }}
      ref={containerRef}
      data-testid={'job-search-min-pay-rate-container'}
      fullWidth
      open={open}
      onOpen={_e => setOpen(true)}
      onClose={e => {
        // disable closing dropdown on input blur
        if (e.type !== 'blur') {
          setOpen(false);
        }
      }}
      options={MIN_PAY_RATES[minPayRateUnit]}
      getOptionLabel={option => `$${option}`}
      autoHighlight={true}
      clearOnBlur={true}
      renderInput={params => (
        <TextField
          {...params}
          label={'Est. minimum pay rate'}
          fullWidth
          inputProps={{
            ...params.inputProps,
            'data-testid': 'job-search-min-pay-rate-input',
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: minPayRate ? (
              <InputAdornment position="start">{minPayRateUnit}:</InputAdornment>
            ) : null,
          }}
        />
      )}
      PaperComponent={({ children, ...rest }) => {
        const getButton = (unit: MinPayRateUnit) => (
          <Button
            onClick={e => {
              if (unit !== minPayRateUnit) {
                onMinPayRateUnitChange(unit);
                onMinPayRateChange(null); // reset minPayRate value when unit is changed
              }
            }}
            size={'small'}
            data-testid={`job-search-min-pay-rate-unit-button-${unit.toLowerCase()}`}
            className={cx(styles.button, minPayRateUnit === unit ? styles.active : null)}
            color={minPayRateUnit === unit ? 'primary' : 'default'}
            variant={minPayRateUnit === unit ? 'contained' : 'outlined'}
            aria-current={minPayRateUnit === unit}
          >
            {unit}
          </Button>
        );
        return (
          <Paper {...rest} ref={paperRef} className={styles.dropdown}>
            <div className={styles.buttonGroup}>
              <ButtonGroup disableElevation fullWidth>
                {getButton('Hourly')}
                {getButton('Daily')}
              </ButtonGroup>
            </div>
            {children}
          </Paper>
        );
      }}
      ListboxProps={{
        style: { maxHeight: 180, overflow: 'auto' },
        'data-testid': 'job-search-min-pay-rate-value-list',
      }}
    />
  );
};

export default MinPayRateDropdown;
